import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, CopyIcon } from 'lucide-react';
import { Reveal } from '../components/Reveal';
import { GridRails } from '../components/GridRails';
import { GridDivider } from '../components/GridDivider';
import { PdfDownloadButton } from '../components/PdfDownloadButton';
import { OrbitFigure } from '../components/OrbitFigure';
import { usePageMeta } from '../hooks/usePageMeta';
import { manuscriptSeo } from '../data/seo';
import { manuscriptMeta as meta, manuscriptAbstract, manuscriptKeywords, manuscriptSections, manuscriptReferences, ManuscriptBlock } from '../data/manuscript';
const inner = 'relative z-10 mx-auto max-w-[1720px] px-5 md:px-16 lg:px-24';
const label = 'font-accent text-[11px] uppercase tracking-[0.08em]';

/** The 0/1 Manuscript, set as a working paper: title block, abstract, numbered sections, references. */
export function Manuscript() {
  usePageMeta(manuscriptSeo);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <>
      {/* Title block on navy, under the header */}
      <section className="relative bg-navy pb-14 pt-[160px] text-cream md:pb-20 md:pt-[220px]" data-id="element-496" data-mp="1lpsw3kxgl33u">
        <GridRails tone="dark" data-id="element-497" data-mp="1ptiic3qnv8lp" />
        <div className={`${inner} px-9`} data-id="element-498" data-mp="1pt8rkdxskxey">
          <Reveal data-id="element-499" data-mp="1us24a4598ke2">
            <p className={`${label} flex items-center gap-3 text-cream/70`} data-id="element-500" data-mp="1uqhaigqrl5h9">
              <span aria-hidden="true" className="inline-block h-[6px] w-[6px] bg-white" data-id="element-501" data-mp="y1t4kisbp8o7" />
              {meta.series}
            </p>
            <h1 className="mt-6 max-w-[980px] font-display text-[40px] leading-[1.02] tracking-[-0.03em] md:text-[64px]" data-id="element-502" data-mp="123o1lb380zw6">
              <span className="font-editorial italic tracking-[-0.015em]" data-id="element-503" data-mp="16zuzqhpdl73k">{meta.title}</span> — {meta.titleRest}
            </h1>
            <p className="mt-7 text-[15px] text-cream/90" data-id="element-504" data-mp="edk13clbxg9e">{meta.authors}</p>
            <p className="mt-1 text-[13px] text-cream/55" data-id="element-505" data-mp="eb7j4zfkn5wc">{meta.affiliation}</p>
          </Reveal>

          <Reveal delay={0.08} data-id="element-506" data-mp="jaaji6klwnxz">
            <dl className="mt-10 grid grid-cols-2 gap-y-5 border-t border-cream/10 pt-6 sm:grid-cols-4 md:max-w-[820px]" data-id="element-507" data-mp="1mmr93qsimqsv">
              {[['Published', meta.published], ['Version', meta.version], ['Length', `${meta.pages} · ${meta.reading}`], ['Identifier', meta.identifier]].map(([k, v]) => <div key={k} data-id="element-508" data-mp="13oez62ts5cw8">
                  <dt className={`${label} text-cream/45`} data-id="element-509" data-mp="13nn3yt43hh6h">{k}</dt>
                  <dd className="mt-1.5 text-[13px] text-cream/85" data-id="element-510" data-mp="ypbtcg5z72n7">{v}</dd>
                </div>)}
            </dl>
            <div className="mt-10 flex flex-wrap gap-3" data-id="element-511" data-mp="1cpd5wsvlryii">
              <PdfDownloadButton href={meta.pdf} data-id="element-512" data-mp="1cn0gxvrfgnvy" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Paper body */}
      <section className="relative bg-white text-ink" data-id="element-513" data-mp="2o3gybumjlelg">
        <GridRails data-id="element-514" data-mp="2o6cvhgqkfz8u" />
        <div className={`${inner} grid gap-12 px-9 py-14 md:grid-cols-[220px_1fr] md:gap-20 md:py-24`} data-id="element-515" data-mp="2j56zna1lzvv3">
          <aside className="md:sticky md:top-12 md:self-start" data-id="element-516" data-mp="2rmfovam5qse8">
            <Reveal data-id="element-517" data-mp="2roadp00jv4nl">
              <p className={`${label} hidden text-ink/50 md:block`} data-id="element-518" data-mp="2vnszdy073jrh">Contents</p>
              <ol className="mt-4 hidden space-y-2 text-[13px] md:block" data-id="element-519" data-mp="2vlokym4o72cy">
                <li data-id="element-520" data-mp="2hpktv1p3fa4p">
                  <a href="#abstract" className="link-soft no-arrow text-ink/70" data-id="element-521" data-mp="2hq4e4p27ualw">
                    Abstract
                  </a>
                </li>
                {manuscriptSections.map(s => <li key={s.id} data-id="element-522" data-mp="3aes0knypb611">
                    <a href={`#${s.id}`} className="link-soft no-arrow flex gap-3 text-ink/70" data-id="element-523" data-mp="3fca0jouqqaww">
                      <span className="w-3 tabular-nums text-ink/40" data-id="element-524" data-mp="30ndws3zpvk23">{s.number}</span>
                      {s.heading}
                    </a>
                  </li>)}
                <li data-id="element-525" data-mp="3tbjjuxe8tl53">
                  <a href="#references" className="link-soft no-arrow text-ink/70" data-id="element-526" data-mp="3tb9r521tczvp">
                    References
                  </a>
                </li>
              </ol>

              <p className={`${label} text-ink/50 md:mt-10`} data-id="element-527" data-mp="29ggqyn8s2ptg">Download</p>
              <PdfDownloadButton href={meta.pdf} tone="dark" className="mt-4 w-full justify-start" data-id="element-528" data-mp="29ecc5a9cc6ee" />
              <p className="mt-2 text-[12px] text-ink/45" data-id="element-529" data-mp="25gy5nqd1ocq3">
                PDF · {meta.pages}
              </p>

              <CiteBlock data-id="element-530" data-mp="20hlif4i9h3mz" />
            </Reveal>
          </aside>

          <article className="max-w-[700px]" data-id="element-531" data-mp="1vi8lts1l8i77">
            {/* Abstract */}
            <Reveal data-id="element-532" data-mp="2t3ee6u553i09">
              <section id="abstract" className="scroll-mt-12 border-y border-mist-line py-8" data-id="element-533" data-mp="2t3wjshdoz8ku">
                <h2 className={`${label} text-ink/80`} data-id="element-534" data-mp="2o6o20hwgh6k4">Abstract</h2>
                <p className="mt-4 text-[15.5px] leading-[1.7] text-ink/80" data-id="element-535" data-mp="2je3bbp8uj9kt">{manuscriptAbstract}</p>
                <p className="mt-6 text-[13px] leading-[1.6] text-ink/60" data-id="element-536" data-mp="2eg1n6pwkck2h">
                  <span className={`${label} mr-3 text-ink/50`} data-id="element-537" data-mp="3buxgszv94u1r">Keywords</span>
                  {manuscriptKeywords.join(' · ')}
                </p>
              </section>
            </Reveal>

            {manuscriptSections.map(s => <section key={s.id} id={s.id} className="mt-14 scroll-mt-12" data-id="element-538" data-mp="3egeyo070z8n5">
                <Reveal data-id="element-539" data-mp="3jdwzf3aw2hk2">
                  <h2 className="flex items-baseline gap-4 font-display text-[24px] leading-[1.2] tracking-[-0.02em] md:text-[28px]" data-id="element-540" data-mp="3jf80uwgv3d80">
                    <span className="font-accent text-[13px] tabular-nums tracking-normal text-ink/40" data-id="element-541" data-mp="3ofm8idejwl5i">{s.number}.</span>
                    {s.heading}
                  </h2>
                </Reveal>
                <div className="mt-5 space-y-5" data-id="element-542" data-mp="16qyghx39i70">
                  {s.blocks.map((b, i) => <Reveal key={i} data-id="element-543" data-mp="65ttran9oa1s" data-mpi={i}>
                      <Block block={b} data-id="element-544" data-mp="ay3iwogk5630" />
                    </Reveal>)}
                </div>
              </section>)}

            {/* References */}
            <section id="references" className="mt-16 scroll-mt-12 border-t border-mist-line pt-10" data-id="element-545" data-mp="kwt2qms0l0lw">
              <Reveal data-id="element-546" data-mp="oyebbvximsqa">
                <h2 className={`${label} text-ink/80`} data-id="element-547" data-mp="oxmpvdxmubdq">References</h2>
                <ol className="mt-5 space-y-3 text-[13.5px] leading-[1.6] text-ink/70" data-id="element-548" data-mp="2lz1zuj2uawhi">
                  {manuscriptReferences.map((r, i) => <li key={r} className="grid grid-cols-[28px_1fr]" data-id="element-549" data-mp="2h18ufs9d26ia" data-mpi={i}>
                      <span className="font-accent text-[11px] tabular-nums text-ink/40" data-id="element-550" data-mp="2urvvhr2lg8hj">[{i + 1}]</span>
                      <span data-id="element-551" data-mp="2q0dskxh0fxtx">{r}</span>
                    </li>)}
                </ol>
              </Reveal>
            </section>

            <Reveal data-id="element-552" data-mp="34np5p3subpw5">
              <div className="mt-14 flex flex-wrap items-center justify-between gap-6 border-t border-mist-line pt-8" data-id="element-553" data-mp="2zqfhfrh6tk9w">
                <Link to="/" className="link-soft no-arrow text-[13px] text-ink/70" data-id="element-554" data-mp="3dqq92wqbn2w4">
                  ← Back to KAYS &amp; KAYS
                </Link>
                <PdfDownloadButton href={meta.pdf} tone="dark" data-id="element-555" data-mp="39peesdwi5thl" />
              </div>
            </Reveal>
          </article>
        </div>
      </section>
      <GridDivider data-id="element-556" data-mp="1fb3os2468ta8" />
    </>;
}
function Block({
  block
}: {
  block: ManuscriptBlock;
}) {
  switch (block.type) {
    case 'p':
      return <p className="text-[15.5px] leading-[1.75] text-ink/80" data-id="element-557" data-mp="1576uczfjwip7">{block.text}</p>;
    case 'eq':
      return <div className="flex items-center justify-between gap-6 py-3" role="math" aria-label={block.aria} data-id="element-558" data-mp="197gye6js8z1f">
          <span className="flex-1 text-center font-editorial text-[26px] italic tracking-[-0.01em] text-ink md:text-[30px]" data-id="element-559" data-mp="vercs3861w4u">
            {block.tex}
          </span>
          <span className="font-accent text-[12px] text-ink/45" data-id="element-560" data-mp="lhwglqzd87tq">({block.label})</span>
        </div>;
    case 'quote':
      return <blockquote className="border-l border-ink/30 py-1 pl-6" data-id="element-561" data-mp="qfehcu38bgqn">
          <p className="font-editorial text-[22px] italic leading-[1.35] text-ink" data-id="element-562" data-mp="cjkg8x8p5vo3">“{block.text}”</p>
          <footer className={`${label} mt-3 text-ink/45`} data-id="element-563" data-mp="hhm9gazu7bda">{block.source}</footer>
        </blockquote>;
    case 'figure':
      return <figure className="border-y border-mist-line py-6" data-id="element-564" data-mp="2butyfm14ssr0">
          <OrbitFigure data-id="element-565" data-mp="2buc4wx5ussms" />
          <figcaption className="mt-4 text-[13px] leading-[1.55] text-ink/60" data-id="element-566" data-mp="2bsr7mzs5mvl8">
            <span className={`${label} mr-2 text-ink/80`} data-id="element-567" data-mp="3kerikfyjc74t">Figure 1.</span>
            {block.caption}
          </figcaption>
        </figure>;
    case 'table':
      return <figure className="py-2" data-id="element-568" data-mp="3ao6isp74kwkb">
          <figcaption className="mb-3 text-[13px] leading-[1.55] text-ink/60" data-id="element-569" data-mp="3andc1o862726">
            <span className={`${label} mr-2 text-ink/80`} data-id="element-570" data-mp="36nw50qd3d1uw">Table 1.</span>
            {block.caption}
          </figcaption>
          <div className="overflow-x-auto" data-id="element-571" data-mp="2wpyetxbo8uzn">
            <table className="w-full min-w-[560px] border-y border-ink/40 text-left text-[13px] leading-[1.45]" data-id="element-572" data-mp="2wox6w1pycom5">
              <thead data-id="element-573" data-mp="2rrdoqqq4vrox">
                <tr className="border-b border-ink/20" data-id="element-574" data-mp="2rsybvva4c8cr">
                  {block.head.map(h => <th key={h} scope="col" className={`${label} px-3 py-3 font-normal text-ink/70 first:pl-0`} data-id="element-575" data-mp="2nrmrq5a0kfby">
                      {h}
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-576" data-mp="23vs4khyccu4g">
                {block.rows.map(r => <tr key={r[0]} className="border-b border-mist-line last:border-b-0" data-id="element-577" data-mp="23tfqifvpf3wo">
                    {r.map((c, i) => <td key={c} className={`px-3 py-3 align-top first:pl-0 ${i === 0 ? 'text-ink' : 'text-ink/70'}`} data-id="element-578" data-mp="2dlljnsxozrdg" data-mpi={i}>
                        {c}
                      </td>)}
                  </tr>)}
              </tbody>
            </table>
          </div>
        </figure>;
  }
}

/** Suggested citation with a copy action. */
function CiteBlock() {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(meta.citation);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };
  return <div className="mt-10" data-id="element-579" data-mp="35elwx1ig0cw9">
      <p className={`${label} text-ink/50`} data-id="element-580" data-mp="3abcddpy5tai7">Cite this paper</p>
      <p className="mt-3 text-[12px] leading-[1.55] text-ink/60" data-id="element-581" data-mp="3adp3iqe2mr6c">{meta.citation}</p>
      <button type="button" onClick={copy} className={`${label} mt-3 inline-flex items-center gap-2 text-ink/70 transition-colors duration-200 hover:text-ink`} data-id="element-582" data-mp="27zimsu776m14">
        {copied ? <CheckIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-583" data-mp="2hqn3pzgfogyl" /> : <CopyIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-584" data-mp="2mpg9f5phb3o7" />}
        {copied ? 'Copied' : 'Copy citation'}
      </button>
      <span className="sr-only" aria-live="polite" data-id="element-585" data-mp="1p2hhf89qejwn">
        {copied ? 'Citation copied' : ''}
      </span>
    </div>;
}